/**
 * LooksLab API client
 *
 * In development the Vite proxy forwards /api/* → localhost:8787 (wrangler dev).
 * In production set VITE_API_URL to your deployed Worker URL, e.g.:
 *   VITE_API_URL=https://lookslab-api.workers.dev
 */

const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
const TOKEN_KEY = 'lookslab_token'

export function getToken() {
  return sessionStorage.getItem(TOKEN_KEY)
}

export function setToken(token) {
  if (token) sessionStorage.setItem(TOKEN_KEY, token)
  else sessionStorage.removeItem(TOKEN_KEY)
}

async function apiFetch(path, options = {}) {
  const token = getToken()
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }))
    const error = new Error(body.error ?? 'Request failed')
    error.status = res.status
    throw error
  }
  return res.json()
}

export const api = {
  // ── Auth ──────────────────────────────────────────────────
  login: (username, password) =>
    apiFetch('/api/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),

  logout: () =>
    apiFetch('/api/auth/logout', { method: 'POST' }),

  // ── Orders ────────────────────────────────────────────────
  getOrders: () =>
    apiFetch('/api/orders'),

  createOrder: (order) =>
    apiFetch('/api/orders', { method: 'POST', body: JSON.stringify(order) }),

  // Staff correction of the customer's own details — name, email, address,
  // Discord, delivery notes. Never items or pricing (see orderEdit.js).
  // `notify` sends the customer a summary of exactly what changed.
  updateOrderDetails: (invoiceNumber, details) =>
    apiFetch(`/api/orders/${invoiceNumber}/details`, {
      method: 'PATCH',
      body: JSON.stringify(details),
    }),

  // `paymentMethod` is only read when status === 'paid'. Omitting it there
  // records the order as Unclassified rather than failing, so nothing that
  // predates the field breaks.
  updateOrderStatus: (invoiceNumber, status, notify = true, cancelReason, paymentMethod) =>
    apiFetch(`/api/orders/${invoiceNumber}/status`, {
      method: 'PATCH',
      body: JSON.stringify({
        status, notify,
        ...(cancelReason  !== undefined ? { cancelReason }  : {}),
        ...(paymentMethod !== undefined ? { paymentMethod } : {}),
      }),
    }),

  // Correcting how an order was paid after the fact, without re-stamping
  // who last changed its status.
  setOrderPaymentMethod: (invoiceNumber, paymentMethod) =>
    apiFetch(`/api/orders/${invoiceNumber}/payment-method`, {
      method: 'PATCH',
      body: JSON.stringify({ paymentMethod }),
    }),

  // Shipments: payload = { carrierId, carrierName, trackingNumber, items: [{ idx, qty }], notify }
  addOrderShipment: (invoiceNumber, payload) =>
    apiFetch(`/api/orders/${invoiceNumber}/shipments`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateOrderShipment: (invoiceNumber, shipmentId, payload) =>
    apiFetch(`/api/orders/${invoiceNumber}/shipments/${shipmentId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteOrderShipment: (invoiceNumber, shipmentId) =>
    apiFetch(`/api/orders/${invoiceNumber}/shipments/${shipmentId}`, { method: 'DELETE' }),

  // Live carrier tracking (USPS/UPS)
  refreshShipmentTracking: (invoiceNumber, shipmentId) =>
    apiFetch(`/api/orders/${invoiceNumber}/shipments/${shipmentId}/refresh`, { method: 'POST' }),

  setShipmentDelivered: (invoiceNumber, shipmentId, delivered, location) =>
    apiFetch(`/api/orders/${invoiceNumber}/shipments/${shipmentId}/delivered`, {
      method: 'PATCH',
      body: JSON.stringify({ delivered, location }),
    }),

  getCarrierApiStatus: () =>
    apiFetch('/api/carrier-apis/status'),

  saveCarrierApis: (payload) =>
    apiFetch('/api/carrier-apis', { method: 'PUT', body: JSON.stringify(payload) }),

  // ── Address autocomplete ──────────────────────────────────
  // Public lookup — the provider key lives on the Worker, never here.
  addressAutocomplete: (q, country = 'US', session, signal) =>
    apiFetch(`/api/address/autocomplete?q=${encodeURIComponent(q)}&country=${encodeURIComponent(country)}${session ? `&session=${encodeURIComponent(session)}` : ''}`, { signal }),

  // Second step for providers (Google) whose suggestions carry needsDetails.
  addressDetails: (id, country = 'US', session) =>
    apiFetch(`/api/address/details?id=${encodeURIComponent(id)}&country=${encodeURIComponent(country)}${session ? `&session=${encodeURIComponent(session)}` : ''}`),

  getAddressApiStatus: () =>
    apiFetch('/api/address/api-status'),

  saveAddressApi: (payload) =>
    apiFetch('/api/address/api', { method: 'PUT', body: JSON.stringify(payload) }),

  // ── Suspicious-information scanner rules ──────────────────
  getRiskConfig: () =>
    apiFetch('/api/risk-config'),

  saveRiskConfig: (config) =>
    apiFetch('/api/risk-config', { method: 'PUT', body: JSON.stringify(config) }),

  // Mark an order's scanner warning as checked (false positive), or undo it.
  clearOrderRisk: (invoiceNumber, note) =>
    apiFetch(`/api/orders/${invoiceNumber}/risk-clear`, { method: 'POST', body: JSON.stringify({ note }) }),

  reopenOrderRisk: (invoiceNumber) =>
    apiFetch(`/api/orders/${invoiceNumber}/risk-clear`, { method: 'DELETE' }),

  previewCheckAllShipments: () =>
    apiFetch('/api/orders/shipments/check-all', { method: 'POST' }),

  applyCheckAllShipments: (payload) =>
    apiFetch('/api/orders/shipments/check-all/apply', { method: 'POST', body: JSON.stringify(payload) }),

  getDeliveryReview: (days) =>
    apiFetch(`/api/orders/delivery-review?days=${encodeURIComponent(days)}`),

  applyDeliveryReview: (payload) =>
    apiFetch('/api/orders/delivery-review/apply', { method: 'POST', body: JSON.stringify(payload) }),

  setFundsDeposited: (invoiceNumber, deposited) =>
    apiFetch(`/api/orders/${invoiceNumber}/funds`, {
      method: 'PATCH',
      body: JSON.stringify({ deposited }),
    }),

  updateOrderNotes: (invoiceNumber, notes) =>
    apiFetch(`/api/orders/${invoiceNumber}/notes`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    }),

  deleteOrder: (invoiceNumber) =>
    apiFetch(`/api/orders/${invoiceNumber}`, { method: 'DELETE' }),

  getInvoiceByToken: (token) =>
    apiFetch(`/api/invoice/${token}`),

  getOrderStatusHistory: (invoiceNumber) =>
    apiFetch(`/api/orders/${invoiceNumber}/history`),

  // ── Order flags (staff-to-staff attention marker + notes) ────
  getOpenFlagCount: () =>
    apiFetch('/api/orders/flags/count'),

  flagOrder: (invoiceNumber, note) =>
    apiFetch(`/api/orders/${invoiceNumber}/flag`, { method: 'POST', body: JSON.stringify({ note }) }),

  resolveOrderFlag: (invoiceNumber, note) =>
    apiFetch(`/api/orders/${invoiceNumber}/flag/resolve`, { method: 'POST', body: JSON.stringify({ note }) }),

  getOrderFlagNotes: (invoiceNumber) =>
    apiFetch(`/api/orders/${invoiceNumber}/flag/notes`),

  addOrderFlagNote: (invoiceNumber, body) =>
    apiFetch(`/api/orders/${invoiceNumber}/flag/notes`, { method: 'POST', body: JSON.stringify({ body }) }),

  // ── Public order tracking ─────────────────────────────────
  trackOrder: (email, invoiceNumber) =>
    apiFetch('/api/track', { method: 'POST', body: JSON.stringify({ email, invoiceNumber }) }),

  // ── Status page ───────────────────────────────────────────
  getStatusPage: () =>
    apiFetch('/api/status-page'),

  saveStatusPage: (data) =>
    apiFetch('/api/status-page', { method: 'PUT', body: JSON.stringify(data) }),

  // ── Inventory ─────────────────────────────────────────────
  getInventory: () =>
    apiFetch('/api/inventory'),

  saveInventory: (inventory) =>
    apiFetch('/api/inventory', { method: 'PUT', body: JSON.stringify(inventory) }),

  receiveStock: (itemKey, quantity, note = '') =>
    apiFetch('/api/inventory/receive', { method: 'POST', body: JSON.stringify({ itemKey, quantity, note }) }),

  adjustStock: (itemKey, quantity, note = '') =>
    apiFetch('/api/inventory/adjust', { method: 'POST', body: JSON.stringify({ itemKey, quantity, note }) }),

  getStockMovements: (itemKey = null, limit = 150) =>
    apiFetch(`/api/inventory/movements?limit=${limit}${itemKey ? `&itemKey=${encodeURIComponent(itemKey)}` : ''}`),

  // ── Admin dashboard ───────────────────────────────────────
  getAdminStats: () =>
    apiFetch('/api/admin/stats'),

  // Bulk order actions. Each order still runs the full single-order path
  // server-side; the response reports per-order success so partial failures
  // are visible rather than swallowed.
  bulkOrderAction: (payload) =>
    apiFetch('/api/orders/bulk', { method: 'POST', body: JSON.stringify(payload) }),

  // ── Refunds ───────────────────────────────────────────────
  getRefunds: (invoiceNumber) =>
    apiFetch(`/api/orders/${encodeURIComponent(invoiceNumber)}/refunds`),

  // restock is [{ idx, qty }] against the order's own item list.
  createRefund: (invoiceNumber, payload) =>
    apiFetch(`/api/orders/${encodeURIComponent(invoiceNumber)}/refunds`, {
      method: 'POST', body: JSON.stringify(payload),
    }),

  deleteRefund: (invoiceNumber, id) =>
    apiFetch(`/api/orders/${encodeURIComponent(invoiceNumber)}/refunds/${id}`, { method: 'DELETE' }),

  // ── Backups ───────────────────────────────────────────────
  getBackupStatus: () =>
    apiFetch('/api/admin/backup/status'),

  runBackupNow: () =>
    apiFetch('/api/admin/backup/run', { method: 'POST' }),

  // Not apiFetch: the dump is a streamed file, not JSON, and the endpoint
  // needs the bearer token so a plain <a href> can't fetch it.
  downloadBackup: async (format = 'sql') => {
    const token = getToken()
    const res = await fetch(`${BASE}/api/admin/backup?format=${format}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.error ?? 'Backup failed')
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1]
      ?? `lookslab-backup.${format}`
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = name
    document.body.appendChild(a); a.click()
    document.body.removeChild(a); URL.revokeObjectURL(url)
    return { name, bytes: blob.size }
  },

  // ── Notifications (admin bar) ─────────────────────────────
  getNotifications: () =>
    apiFetch('/api/admin/notifications'),

  // fingerprint pins the dismissal to the situation the staff member saw, so
  // it resurfaces if that situation changes. snoozeMinutes hides it outright
  // for a while instead.
  dismissNotification: (id, fingerprint, snoozeMinutes) =>
    apiFetch('/api/admin/notifications/dismiss', {
      method: 'POST',
      body: JSON.stringify({ id, fingerprint, ...(snoozeMinutes ? { snoozeMinutes } : {}) }),
    }),

  restoreNotifications: (id) =>
    apiFetch('/api/admin/notifications/restore', { method: 'POST', body: JSON.stringify(id ? { id } : {}) }),

  // Settings → Overview: one wide snapshot of every number in the system.
  getBusinessOverview: () =>
    apiFetch('/api/admin/overview'),

  getAdminActivity: (limit = 200) =>
    apiFetch(`/api/admin/activity?limit=${limit}`),

  undoActivity: (id) =>
    apiFetch(`/api/admin/activity/${id}/undo`, { method: 'POST' }),

  getEmailUsage: () =>
    apiFetch('/api/admin/email-usage'),

  // ── Expenses (Revenue tab) ────────────────────────────────
  getExpenses: () =>
    apiFetch('/api/expenses'),

  addExpense: (data) =>
    apiFetch('/api/expenses', { method: 'POST', body: JSON.stringify(data) }),

  deleteExpense: (id) =>
    apiFetch(`/api/expenses/${id}`, { method: 'DELETE' }),

  // ── Discount codes ────────────────────────────────────────
  getCodes: () =>
    apiFetch('/api/codes'),

  saveCodes: (codes) =>
    apiFetch('/api/codes', { method: 'PUT', body: JSON.stringify(codes) }),

  validateCode: (code, subtotal) =>
    apiFetch('/api/codes/validate', {
      method: 'POST',
      body: JSON.stringify({ code, subtotal }),
    }),

  // ── Products ──────────────────────────────────────────────
  getProducts: () =>
    apiFetch('/api/products'),

  getAllProducts: () =>
    apiFetch('/api/products/all'),

  createProduct: (data) =>
    apiFetch('/api/products', { method: 'POST', body: JSON.stringify(data) }),

  updateProduct: (id, data) =>
    apiFetch(`/api/products/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteProduct: (id) =>
    apiFetch(`/api/products/${id}`, { method: 'DELETE' }),

  // ── Catalog settings ──────────────────────────────────────
  getCatalogSettings: () =>
    apiFetch('/api/catalog-settings'),

  saveCatalogSettings: (settings) =>
    apiFetch('/api/catalog-settings', { method: 'PUT', body: JSON.stringify(settings) }),

  // ── Newsletter ────────────────────────────────────────────
  subscribeNewsletter: (email, turnstileToken = null, firstName = undefined) =>
    apiFetch('/api/newsletter', { method: 'POST', body: JSON.stringify({ email, firstName, turnstileToken }) }),

  getNewsletterContacts: () =>
    apiFetch('/api/newsletter'),

  deleteNewsletterContact: (contactId) =>
    apiFetch(`/api/newsletter/${contactId}`, { method: 'DELETE' }),

  getCustomerContacts: () =>
    apiFetch('/api/email/customers'),

  deleteCustomerContact: (contactId) =>
    apiFetch(`/api/email/customers/${contactId}`, { method: 'DELETE' }),

  sendEmailCampaign: (payload) =>
    apiFetch('/api/email/send', { method: 'POST', body: JSON.stringify(payload) }),

  previewEmailCampaign: (payload) =>
    apiFetch('/api/email/preview', { method: 'POST', body: JSON.stringify(payload) }),

  previewEmailTemplate: (payload) =>
    apiFetch('/api/email/template-preview', { method: 'POST', body: JSON.stringify(payload) }),

  // ── Inbox ─────────────────────────────────────────────────
  getInbox: () =>
    apiFetch('/api/inbox'),

  getInboxEmail: (id) =>
    apiFetch(`/api/inbox/${id}`),

  patchInboxEmail: (id, data) =>
    apiFetch(`/api/inbox/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteInboxEmail: (id) =>
    apiFetch(`/api/inbox/${id}`, { method: 'DELETE' }),

  replyToInboxEmail: (id, payload) =>
    apiFetch(`/api/inbox/${id}/reply`, { method: 'POST', body: JSON.stringify(payload) }),

  previewInboxReply: (id, payload) =>
    apiFetch(`/api/inbox/${id}/reply/preview`, { method: 'POST', body: JSON.stringify(payload) }),

  getInboxThread: (id) =>
    apiFetch(`/api/inbox/${id}/thread`),

  getEmailLog: (params = {}) =>
    apiFetch(`/api/email-log?${new URLSearchParams(params).toString()}`),

  // ── Customers (accounts platform) ─────────────────────────
  getCustomers: () =>
    apiFetch('/api/customers'),

  getCustomer: (id) =>
    apiFetch(`/api/customers/${id}`),

  updateCustomer: (id, data) =>
    apiFetch(`/api/customers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  addCustomerNote: (id, body) =>
    apiFetch(`/api/customers/${id}/notes`, { method: 'POST', body: JSON.stringify({ body }) }),

  deleteCustomerNote: (id, noteId) =>
    apiFetch(`/api/customers/${id}/notes/${noteId}`, { method: 'DELETE' }),

  sendCustomerPasswordReset: (id) =>
    apiFetch(`/api/customers/${id}/reset-password`, { method: 'POST' }),

  linkCustomerOrder: (id, invoiceNumber) =>
    apiFetch(`/api/customers/${id}/link-order`, { method: 'POST', body: JSON.stringify({ invoiceNumber }) }),

  unlinkCustomerOrder: (id, invoiceNumber) =>
    apiFetch(`/api/customers/${id}/unlink-order`, { method: 'POST', body: JSON.stringify({ invoiceNumber }) }),

  mergeCustomers: (keepId, mergeId) =>
    apiFetch('/api/customers/merge', { method: 'POST', body: JSON.stringify({ keepId, mergeId }) }),

  recalcCustomer: (id) =>
    apiFetch(`/api/customers/${id}/recalc`, { method: 'POST' }),

  impersonateCustomer: (id) =>
    apiFetch(`/api/customers/${id}/impersonate`, { method: 'POST' }),

  deleteCustomer: (id) =>
    apiFetch(`/api/customers/${id}`, { method: 'DELETE' }),

  // ── Rewards platform (admin) ──────────────────────────────
  getRewardsAnalytics: () =>
    apiFetch('/api/rewards/analytics'),

  adjustCustomerPoints: (id, delta, note) =>
    apiFetch(`/api/customers/${id}/points`, { method: 'POST', body: JSON.stringify({ delta, note }) }),

  adjustCustomerCredit: (id, delta, note) =>
    apiFetch(`/api/customers/${id}/credit`, { method: 'POST', body: JSON.stringify({ delta, note }) }),

  adjustCustomerReferralCash: (id, delta, note) =>
    apiFetch(`/api/customers/${id}/referral-cash`, { method: 'POST', body: JSON.stringify({ delta, note }) }),

  getCustomerLedger: (id) =>
    apiFetch(`/api/customers/${id}/ledger`),

  getReferralEvents: (referrerId) =>
    apiFetch(`/api/referrals${referrerId ? `?referrerId=${encodeURIComponent(referrerId)}` : ''}`),

  getReferralEventsForOrder: (invoiceNumber) =>
    apiFetch(`/api/referrals?invoiceNumber=${encodeURIComponent(invoiceNumber)}`),

  decideReferral: (id, decision) =>
    apiFetch(`/api/referrals/${id}/${decision}`, { method: 'POST' }),

  getReferralPayouts: () =>
    apiFetch('/api/referral-payouts'),

  completeReferralPayout: (id) =>
    apiFetch(`/api/referral-payouts/${id}/complete`, { method: 'POST' }),

  denyReferralPayout: (id, reason) =>
    apiFetch(`/api/referral-payouts/${id}/deny`, { method: 'POST', body: JSON.stringify({ reason }) }),

  // ── Admin accounts ────────────────────────────────────────
  getAccounts: () =>
    apiFetch('/api/admin/accounts'),

  createAccount: (data) =>
    apiFetch('/api/admin/accounts', { method: 'POST', body: JSON.stringify(data) }),

  updateAccount: (id, data) =>
    apiFetch(`/api/admin/accounts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  deleteAccount: (id) =>
    apiFetch(`/api/admin/accounts/${id}`, { method: 'DELETE' }),
}
